import { EProjectType, EUserAccessPart, IProjectUserRole, IUserInfoDto, KAgentHelper } from "@kuika/kuika-cl-model";
import { IProjectsResponseDto, ITemplateProjectDto } from "@kuika/kuika-cl-model/src/dtos/projects-response";
import { EProjectFeature } from "@kuika/kuika-cl-model/src/models/project-type";
import dayjs from "dayjs";
import { Guid } from "guid-typescript";
import React from "react";
import { connect } from "react-redux";
import { ProjectService } from "../../../../services/project-service";
import { UserService } from "../../../../services/user-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { getProjectEntryScreenId } from "../../../../shared/utility/project-entry-screen";
import { dispatchProjectListChanged } from "../../../../shared/utility/project-list-events";
import { TitleManager } from "../../../../shared/utility/title-manager";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import { setProjectCounts } from "../../../../store/management-layout/actions";
import { ISetProjectCountsParams } from "../../../../store/management-layout/types";
import ProjectIcon from "../../../../svgs/small-icons/project-icon.svg";
import ArchiveSvg from "../../../../svgs/template-project-icons/archive";
import AutomationSvg from "../../../../svgs/template-project-icons/automation";
import CalendarTimeSvg from "../../../../svgs/template-project-icons/calendar-time";
import DatabaseSvg from "../../../../svgs/template-project-icons/database";
import DeviceDesktopSvg from "../../../../svgs/template-project-icons/device-desktop-cog";
import FileTextSvg from "../../../../svgs/template-project-icons/file-text";
import MailOpenedSvg from "../../../../svgs/template-project-icons/mail-opened";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaAvatar, { getIdentitySeed } from "../../../ui-kit/kuika-avatar/kuika-avatar";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTable, { KuikaTableColumn } from "../../../ui-kit/kuika-table/kuika-table";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import KuikaTitle from "../../../ui-kit/typography/Title/Title";
import { ProjectAppIcon } from "../project-app-icon/project-app-icon";
import { ProjectDefinitionAddWithProjectType } from "../project-definition/project-definition-add-with-project-type";
import { ProjectDuplicateModal } from "../project-definition/project-duplicate-modal";
import { TransferProjectOwnershipModal } from "../project-definition/transfer-project-ownership-modal";

declare let window: any;

// Runtime shape of userProjectRoles entries (the DTO types them minimally). The
// backend fills profilePhotoUrl here but not on projectOwner, so we look the
// owner's photo up by userId.
interface IProjectRoleUserPhoto {
  userId: string;
  profilePhotoUrl?: string;
}

// userProjectRoles carries names under user*-prefixed keys that IUserInfoDto lacks.
interface ISharedUserEntry extends IUserInfoDto {
  userFirstName?: string;
  userLastName?: string;
}

export interface IProjectListBaseState {
  projectList: IProjectsResponseDto[];
  isDataLoading: boolean;
  isTemplatesLoading: boolean;
  selectedProject: IProjectsResponseDto;
  compSetId: string;
  searchKeyWords: string[];
  searchText: string;
  projectModalVisibility: boolean;
  projectToEdit: IProjectsResponseDto;
  triggerProjectDeleteModal: boolean;
  projectToDelete: IProjectsResponseDto;
  isDuplicateProjectModalActive: boolean;
  projectIdToDuplicate: string;
  projectToDuplicate: IProjectsResponseDto;
  isTransferOwnershipModalActive: boolean;
  projectIdToTransfer: string;
  projectNameToTransfer: string;
  sharedUsersForTransfer: IProjectUserRole[];
  templateProjects: ITemplateProjectDto[];
  templateProjectType: "all" | "web" | "mobile";
  myAppsType: "all" | "web" | "mobile";
}

export interface IProjectListBaseProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
  setProjectCounts: (params: ISetProjectCountsParams) => void;
  projectListType: EProjectListType;
}

/** The template tabs in their on-screen order, so a pane slides the way the selection moved. */
const TEMPLATE_TYPE_ORDER: string[] = ["all", "web", "mobile"];

export enum EProjectListType {
  Recent = "Recent",
  All = "All",
  Favorites = "Favorites",
  MyProjects = "My",
  Archive = "Archive",
  Templates = "Templates"
}

interface ITemplateIncludesBadgeProps {
  count: number;
  content: React.ReactNode;
}

// Self-contained hover trigger for a template card's "Template Includes" popover. Open state
// is LOCAL to each badge on purpose: seed templates can share a projectId, so a single shared
// "open id" on the parent would reveal the wrong card's popover (or several at once). Local
// state also avoids re-rendering the whole card grid on every hover. onClick stops propagation
// so peeking the badge never fires the card's create-project handler.
const TemplateIncludesBadge = ({ count, content }: ITemplateIncludesBadgeProps): JSX.Element => {
  const [open, setOpen] = React.useState<boolean>(false);
  return (
    <KuikaPopover
      position="bottom"
      hideCloseIcon
      visible={open}
      onVisibleChange={(visible: boolean) => setOpen(visible)}
      content={content}
    >
      <div
        className="kuika-project-list__template-card__includes-badge"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <KuikaSvg name="stack-2" type="common" width={14} height={14} color="var(--white)" />
        <KuikaSmallMediumText className="kuika-project-list__template-card__includes-count">
          {count}
        </KuikaSmallMediumText>
      </div>
    </KuikaPopover>
  );
};

const sortSharedUsers = (a: ISharedUserEntry, b: ISharedUserEntry): number => {
  const hasPhotoA: number = a.profilePhotoUrl ? 1 : 0;
  const hasPhotoB: number = b.profilePhotoUrl ? 1 : 0;
  if (hasPhotoA !== hasPhotoB) {
    return hasPhotoB - hasPhotoA;
  }
  return KMainFunctions.getUserLettersToShow(a).localeCompare(KMainFunctions.getUserLettersToShow(b));
};

interface ISharedUsersPopoverProps {
  users: ISharedUserEntry[];
}

// Open state is local so hovering never re-renders the list — a parent re-render
// would hand KuikaTable a new data array and reset pagination to page 1.
const SharedUsersPopover = ({ users }: ISharedUsersPopoverProps): JSX.Element => {
  const [open, setOpen] = React.useState<boolean>(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = (): void => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openNow = (): void => {
    cancelClose();
    setOpen(true);
  };
  const scheduleClose = (): void => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, 120);
  };
  React.useEffect(() => () => cancelClose(), []);

  return (
    <KuikaPopover
      position="top"
      hideCloseIcon
      collisionPadding={16}
      visible={open}
      onVisibleChange={(visible: boolean) => setOpen(visible)}
      content={
        <div className="kuika_project-list__shared-users-tooltip" onMouseEnter={openNow} onMouseLeave={scheduleClose}>
          {[...users].sort(sortSharedUsers).map((user: ISharedUserEntry, index: number) => (
            <div key={index} className="kuika_project-list__shared-users-tooltip__item">
              <KuikaAvatar size={28} style={{ marginRight: 8 }} src={user.profilePhotoUrl} seed={getIdentitySeed(user)}>
                {KMainFunctions.getUserLettersToShow(user)}
              </KuikaAvatar>
              <span>{`${user.userFirstName} ${user.userLastName}`}</span>
            </div>
          ))}
        </div>
      }
    >
      <div className="kuika_project-list__shared-users" onMouseEnter={openNow} onMouseLeave={scheduleClose}>
        {users
          .slice(0, 4)
          .sort(sortSharedUsers)
          .map((user: ISharedUserEntry, index: number) => (
            <KuikaAvatar
              key={index}
              size={20}
              className="kuika_project-list__shared-users__face"
              src={user.profilePhotoUrl}
              seed={getIdentitySeed(user)}
            >
              {KMainFunctions.getUserLettersToShow(user)}
            </KuikaAvatar>
          ))}
        {users.length > 4 && (
          // A count, not a person: it stays the neutral grey and takes no identity colour.
          <KuikaAvatar size={20} className="kuika_project-list__shared-users__face">
            +{users.length - 4}
          </KuikaAvatar>
        )}
      </div>
    </KuikaPopover>
  );
};

class ProjectListBase extends React.PureComponent<
  IProjectListBaseProps & IWithContextProp & IWithHistoryProp,
  IProjectListBaseState
> {
  constructor(props: IProjectListBaseProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = {
      projectList: [],
      isDataLoading: false,
      isTemplatesLoading: false,
      selectedProject: {} as IProjectsResponseDto,
      compSetId: "",
      searchKeyWords: [],
      searchText: "",
      projectModalVisibility: false,
      projectToEdit: {} as IProjectsResponseDto,
      triggerProjectDeleteModal: false,
      projectToDelete: { name: "" } as IProjectsResponseDto,
      isDuplicateProjectModalActive: false,
      projectIdToDuplicate: "",
      projectToDuplicate: {} as IProjectsResponseDto,
      isTransferOwnershipModalActive: false,
      projectIdToTransfer: "",
      projectNameToTransfer: "",
      sharedUsersForTransfer: [],
      templateProjects: [],
      templateProjectType: "all",
      myAppsType: "all"
    };
  }

  async componentDidMount() {
    if (KMainFunctions.getIsMobile()) {
      this.props.navigator.gotoUnsupportedDevice();
      return;
    }
    this.setState({ isDataLoading: true });
    TitleManager.updateTitle();
    await Promise.all([this.loadTeplateProjects(), this.loadProjectList()]);
  }

  componentDidUpdate(prevProps: IProjectListBaseProps & IWithContextProp & IWithHistoryProp) {
    const dataChangedQuery = new URLSearchParams(this.props.navigator.location.search).get("dataChanged");
    if (prevProps.projectListType !== this.props.projectListType || dataChangedQuery === "true") {
      this.loadProjectList();
      this.loadTeplateProjects();
    }
  }

  async loadProjectList() {
    if (this.props.navigator.location.search.includes("dataChanged=true")) {
      this.props.navigator.navigate(this.props.navigator.location.pathname);
    }
    try {
      this.setState({ isDataLoading: true });
      let projectList: IProjectsResponseDto[] = [];

      switch (this.props.projectListType) {
        case EProjectListType.Recent:
          projectList = (await ProjectService.GetRecentProjects()).data;
          break;
        case EProjectListType.All:
          projectList = (await ProjectService.GetAll()).data;
          break;
        case EProjectListType.Favorites:
          projectList = (await ProjectService.GetFavoriteProjects()).data;
          break;
        case EProjectListType.MyProjects:
          projectList = (await ProjectService.GetMyProjects()).data;
          break;
        case EProjectListType.Archive:
          projectList = (await ProjectService.GetBackups()).data;
        default:
          break;
      }
      if (!projectList || projectList?.length === 0) {
        document?.getElementsByClassName("kuika_table_wrapper")[0]?.classList?.add("kuika_table_placeholder");
      }
      this.setState({ projectList });

      // Keep the left-nav badge exact for free: the list we just fetched IS the My Apps /
      // Favorites source, so push its length to the shared count store (no extra request).
      if (Array.isArray(projectList)) {
        if (this.props.projectListType === EProjectListType.MyProjects) {
          this.props.setProjectCounts({ myProjectsCount: projectList.length });
        } else if (this.props.projectListType === EProjectListType.Favorites) {
          this.props.setProjectCounts({ favoritesCount: projectList.length });
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isDataLoading: false });
    }
  }

  async loadTeplateProjects() {
    try {
      this.setState({ isTemplatesLoading: true });
      let limit = 0;
      if (this.props.projectListType !== EProjectListType.Templates) limit = 8;
      const templateProjects = (await ProjectService.GetTemplateProjects(limit)).data;
      this.setState({ templateProjects });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isTemplatesLoading: false });
    }
  }

  handleSearchInputChange = (e: any) => {
    this.setState({
      searchKeyWords: e.target.value.split(" "),
      searchText: e.target.value
    });
  };

  closeProjectAddModal = async (isReloadRequired: boolean) => {
    if (isReloadRequired === true) {
      this.setState({ projectModalVisibility: false }, this.loadProjectList);
      // A new app was created → refresh the left-nav badge counts.
      dispatchProjectListChanged();
    } else {
      this.setState({ projectModalVisibility: false });
    }
  };

  isUserHasAccess = (roleCode: string) => {
    const result = KMainFunctions.hasUserAccessInWorkspace(roleCode);
    return result === true;
  };

  isUserHasAccessByProjectRole = (roleCode: string, project: IProjectsResponseDto) => {
    const result = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(roleCode, project.projectAccess);
    return result === true;
  };

  openProjectAddModal = async () => {
    this.setState({
      projectModalVisibility: true,
      projectToEdit: {} as IProjectsResponseDto
    });
  };

  createTemplateProject(templateId: string) {
    const templateProject = this.state.templateProjects.find((x) => x.projectId === templateId);
    if (!templateProject) return;
    this.setState({
      projectModalVisibility: true,
      projectToEdit: {
        name: templateProject.name?.replaceAll(" ", "") ?? "",
        projectType: templateProject.type == EProjectType.Desktop ? "web" : "mobile",
        templateProjectId: templateProject.projectId
      } as IProjectsResponseDto
    });
  }

  handleFavoriteClick = async (text) => {
    try {
      const res = await ProjectService.SetProjectFavoriteStatus(text.id, !text.isProjectMyFavorite);
      if (res && res.status === 200) {
        this.loadProjectList();
        dispatchProjectListChanged();
      }
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    }
  };

  getProjectType = (project: IProjectsResponseDto): EProjectType => {
    if (project.projectType === EProjectType.Desktop.toString()) {
      return EProjectType.Desktop;
    }

    if (project.projectType === EProjectType.Mobile.toString()) {
      return EProjectType.Mobile;
    }

    return EProjectType.Desktop;
  };

  // Collapse duplicate role rows so a user holding more than one project role is shown once. Backend
  // returns one row per (user, role), so a multi-role user would otherwise appear as duplicate avatars
  // and inflate the "+N" overflow count.
  getUniqueSharedUsers = (users: IProjectUserRole[]): IProjectUserRole[] => {
    const byUserId: Map<string, IProjectUserRole> = new Map<string, IProjectUserRole>();
    (users ?? []).forEach((user: IProjectUserRole) => {
      if (!byUserId.has(user.userId)) {
        byUserId.set(user.userId, user);
      }
    });
    return Array.from(byUserId.values());
  };

  getTableColumns = (): KuikaTableColumn<IProjectsResponseDto>[] => {
    const isDomainAuthorizationEnabled = KMainFunctions.isDomainAuthorizationEnabled();
    const isArchivePage = this.props.projectListType === EProjectListType.Archive;

    // Shared/Created/Modified are hidden on the Archive page, so build them conditionally.
    const sharedAndCreatedColumns: KuikaTableColumn<IProjectsResponseDto>[] = isArchivePage
      ? []
      : [
          {
            id: "shared",
            header: () => "Shared",
            enableSorting: false,
            width: 144,
            noWrap: true,
            cell: (record: IProjectsResponseDto) => {
              // Dedup multi-role rows (getUniqueSharedUsers) then hand off to the self-contained
              // popover; its open state is local so hovering never resets table pagination.
              const sharedUsers: ISharedUserEntry[] = this.getUniqueSharedUsers(
                record.userProjectRoles ?? []
              ) as unknown as ISharedUserEntry[];
              return <SharedUsersPopover users={sharedUsers} />;
            }
          },
          {
            id: "createdAt",
            header: () => "Created",
            accessorFn: (row: IProjectsResponseDto) => (row.createdAt ? dayjs(row.createdAt).format("YYYY-MM-DD") : ""),
            enableSorting: true,
            enableColumnFilter: true,
            filterVariant: "multiselect",
            filterValueLabel: (value: unknown) => (value ? String(value) : "—"),
            width: 120,
            noWrap: true,
            cell: (record: IProjectsResponseDto) => (
              <KuikaTextMedium className="kuika_project-list__created-cell">
                {dayjs(record.createdAt).fromNow()}
              </KuikaTextMedium>
            )
          },
          {
            id: "modified",
            header: () => "Modified",
            accessorFn: (row: IProjectsResponseDto) =>
              row.lastModified ? dayjs(row.lastModified).format("YYYY-MM-DD") : "",
            enableSorting: true,
            enableColumnFilter: true,
            filterVariant: "multiselect",
            filterValueLabel: (value: unknown) => (value ? String(value) : "—"),
            width: 120,
            noWrap: true,
            // Mirrors the Created cell; "—" when the project has no recorded screen edits yet.
            cell: (record: IProjectsResponseDto) => (
              <KuikaTextMedium className="kuika_project-list__modified-cell">
                {record.lastModified ? dayjs(record.lastModified).fromNow() : "—"}
              </KuikaTextMedium>
            )
          }
        ];

    const columns: KuikaTableColumn<IProjectsResponseDto>[] = [
      {
        id: "name",
        header: () => "App Name",
        headerTestId: "appNameColumnHeader",
        accessorFn: (row: IProjectsResponseDto) => row.name ?? "",
        enableSorting: true,
        enableColumnFilter: false,
        // Narrowed from the Figma 310 (102-7575) to make room for the Modified column
        // without shrinking Created or the action column (table-layout is fixed/100%).
        width: 250,
        noWrap: true,
        cell: (record: IProjectsResponseDto) => (
          <div
            className="kuika_project_table_name"
            style={{ paddingLeft: 0 }}
            onClick={() => {
              if (this.props.projectListType === EProjectListType.Archive) return;

              this.props.navigator.gotoScreen(getProjectEntryScreenId(record));
            }}
          >
            <ProjectAppIcon project={record} />
            <KuikaTextMedium className="project-name" data-testid="projectName">
              {record.name}
            </KuikaTextMedium>
          </div>
        )
      },
      {
        id: "type",
        header: () => "Type",
        accessorFn: (row: IProjectsResponseDto) => row.projectType ?? "",
        enableSorting: true,
        enableColumnFilter: false,
        width: 60,
        noWrap: true,
        cell: (record: IProjectsResponseDto) => {
          const projectType: EProjectType = this.getProjectType(record);
          const typeIconName: string = projectType === EProjectType.Desktop ? "screen" : "device-mobile";
          return (
            <div className="kuika-flex-center">
              <KuikaSvg name={typeIconName} type="common" width={20} height={20} color="var(--dark-7)" />
            </div>
          );
        }
      },
      {
        id: "owner",
        header: () => "Owner",
        accessorFn: (row: IProjectsResponseDto) => row.projectOwner?.firstName ?? row.projectOwner?.username ?? "",
        enableSorting: true,
        enableColumnFilter: false,
        width: 202,
        noWrap: true,
        cell: (record: IProjectsResponseDto) => {
          const owner: IUserInfoDto = record.projectOwner;
          const ownerName: string = owner.firstName ? `${owner.firstName} ${owner.lastName ?? ""}` : owner.username;
          // projectOwner has no profilePhotoUrl from the backend; the owner's photo
          // lives in userProjectRoles (matched by userId). Fall back to initials.
          const roleEntries: IProjectRoleUserPhoto[] = (record.userProjectRoles ??
            []) as unknown as IProjectRoleUserPhoto[];
          const ownerRole: IProjectRoleUserPhoto | undefined = roleEntries.find(
            (u: IProjectRoleUserPhoto): boolean => u.userId === owner.id
          );
          const ownerPhotoUrl: string | undefined = owner.profilePhotoUrl || ownerRole?.profilePhotoUrl;
          return (
            <div className="kuika_project-list__owner-cell">
              <KuikaAvatar
                size={20}
                src={ownerPhotoUrl}
                seed={getIdentitySeed(owner)}
                className="kuika_project-list__owner-avatar"
              >
                {KMainFunctions.getUserLettersToShow(owner)}
              </KuikaAvatar>
              <KuikaTextMedium data-testid="projectOwnerName" className="kuika_project-list__owner-name">
                {ownerName}
              </KuikaTextMedium>
            </div>
          );
        }
      },
      ...sharedAndCreatedColumns,
      {
        id: "edit",
        header: () => "",
        enableSorting: false,
        align: "right",
        width: 88,
        noWrap: true,
        cell: (record: IProjectsResponseDto) => (
          <div className="kuika-flex-center--gap-s">
            {!isArchivePage && (
              <div
                role="button"
                data-testid="appFavoriteButton"
                onClick={() => this.handleFavoriteClick(record)}
                className={`kuika_table_favorite_icon kuika-flex kuika-align-center kuika-pointer ${
                  record.isProjectMyFavorite === true ? "kuika_table_favorite_icon--active" : ""
                }`}
              >
                <KuikaSvg
                  name={record.isProjectMyFavorite === true ? "star-filled" : "star"}
                  type="common"
                  width={16}
                  height={16}
                  color={record.isProjectMyFavorite === true ? "var(--orange-3)" : "var(--dark-5)"}
                  pointer
                />
              </div>
            )}
            {(!isDomainAuthorizationEnabled ||
              (isDomainAuthorizationEnabled &&
                (record.projectOwner.id === this.props.context.userInfo.id ||
                  KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
                    EUserAccessPart.TransferProjectOwnership,
                    record.projectAccess
                  ))) ||
              this.isUserHasEditAccessForProject(record) ||
              this.isUserHasDeleteAccessForProject(record)) && (
              <KuikaDropdownMenu
                options={this.getActionMenuOptions(record)}
                overlayClassName="kuika_project-list__actions-menu"
              >
                <div
                  data-testid="appDotMenuButton"
                  data-onboarding-id="projects_list_app-menu"
                  className="kuika_project-list__dots-btn kuika_table_dot_menu"
                >
                  <KuikaSvg name="dots" type="common" width={16} height={16} color="var(--dark-7)" pointer />
                </div>
              </KuikaDropdownMenu>
            )}
          </div>
        )
      }
    ];

    return columns;
  };

  getActionMenuOptions = (p: IProjectsResponseDto): KuikaDropdownMenuItemType[] => {
    const hasUserAccessTransferOwnership = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.TransferProjectOwnership,
      p.projectAccess
    );
    const hasDuplicateProjectAccess = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.DuplicateProject,
      p.projectAccess
    );
    const isDomainAuthorizationEnabled = KMainFunctions.isDomainAuthorizationEnabled();
    const isArchivePage = this.props.projectListType === EProjectListType.Archive;

    const options: KuikaDropdownMenuItemType[] = [];

    if (
      (hasUserAccessTransferOwnership || p.projectOwner.id === this.props.context.userInfo.id) &&
      isDomainAuthorizationEnabled &&
      !isArchivePage
    ) {
      options.push({
        value: "transfer",
        label: (
          <span data-testid="transferOwnershipButton" data-onboarding-id="projects_list_transfer-ownership">
            Transfer Ownership
          </span>
        ),
        icon: <KuikaSvg name="user-circle" type="common" width={12} height={12} color="var(--dark-7)" />,
        onClick: () => this.openTransferOwnershipModal(p)
      });
    }
    if (this.isUserHasEditAccessForProject(p) && !isArchivePage) {
      options.push({
        value: "edit",
        label: (
          <span data-testid="editbutton" data-onboarding-id="projects_list_edit">
            Edit
          </span>
        ),
        icon: <KuikaSvg name="edit" type="common" width={12} height={12} color="var(--dark-7)" />,
        onClick: () => this.editProject(p)
      });
    }
    if (hasDuplicateProjectAccess) {
      options.push({
        value: "duplicate",
        label: (
          <span data-testid="duplicateButton" data-onboarding-id="projects_list_duplicate">
            {isArchivePage ? "Restore" : "Duplicate"}
          </span>
        ),
        icon: <KuikaSvg name="copy-plus" type="common" width={12} height={12} color="var(--dark-7)" />,
        onClick: () => this.duplicateProject(p)
      });
    }
    if (this.isUserHasDeleteAccessForProject(p)) {
      options.push({
        value: "delete",
        danger: true,
        label: (
          <span data-testid="deleteButton" data-onboarding-id="projects_list_delete">
            Delete
          </span>
        ),
        icon: <KuikaSvg name="trash" type="common" width={12} height={12} color="var(--dark-7)" />,
        onClick: () => this.triggerDeleteProject(p)
      });
    }

    return options;
  };

  openTransferOwnershipModal = (p) => {
    this.setState({
      isTransferOwnershipModalActive: true,
      projectIdToTransfer: p.id,
      projectNameToTransfer: p.name,
      sharedUsersForTransfer: p.userProjectRoles
    });
  };

  isUserHasEditAccessForProject = (project: IProjectsResponseDto) => {
    if (this.isUserHasAccessByProjectRole(EUserAccessPart.EditProject, project) === true) return true;
    if (this.isUserHasAccess(EUserAccessPart.EditProject) === true) return true;

    if (project.amIOwnerOfThisProject) return true;
    if (project.haveIEditAccess === true) return true;
    return false;
  };

  isUserHasDeleteAccessForProject = (project: IProjectsResponseDto) => {
    if (this.isUserHasAccessByProjectRole(EUserAccessPart.DeleteProject, project) === true) return true;
    if (this.isUserHasAccess(EUserAccessPart.DeleteProject) === true) return true;

    if (project.amIOwnerOfThisProject) return true;
    if (project.haveIDeleteAccess === true) return true;
    return false;
  };

  editProject(project: IProjectsResponseDto) {
    this.setState({ projectToEdit: project, projectModalVisibility: true });
  }

  async duplicateProject(project: IProjectsResponseDto) {
    this.setState({
      isDuplicateProjectModalActive: true,
      projectIdToDuplicate: project.id,
      projectToDuplicate: project
    });
  }

  closeDuplicateProjectModal = async (isReloadRequired: boolean) => {
    if (isReloadRequired === true) {
      this.setState({ isDuplicateProjectModalActive: false }, () => {
        if (this.props.projectListType === EProjectListType.Archive) {
          this.props.navigator.gotoRecent();
          return;
        }
        this.loadProjectList();
        dispatchProjectListChanged();
      });
    } else {
      this.setState({ isDuplicateProjectModalActive: false });
    }
  };

  closeTransferOwnershipModal = async (isReloadRequired: boolean) => {
    if (isReloadRequired === true) {
      this.setState({ isTransferOwnershipModalActive: false }, this.loadProjectList);
      // Ownership changed → My Apps count may drop; refresh the left-nav badges.
      dispatchProjectListChanged();
    } else {
      this.setState({ isTransferOwnershipModalActive: false });
    }
  };

  async deleteProject() {
    try {
      const res = await ProjectService.Delete(this.state.projectToDelete.id);
      if (res && res.status === 200) {
        this.loadProjectList();
        this.updateUserInfo();
        dispatchProjectListChanged();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  }

  updateUserInfo = async () => {
    try {
      const res = await UserService.GetUserInfoForce();
      if (res) {
        this.props.context?.setUserInfo(res);
      }
    } catch (error) {
      console.error("Error updating user:", error);
      KMainFunctions.displayErrorNotification("An error occurred while updating user");
    }
  };

  triggerDeleteProject(project: IProjectsResponseDto) {
    this.setState({
      triggerProjectDeleteModal: true,
      projectToDelete: project
    });
  }

  getFeatureIcon = (feature: EProjectFeature, color = "var(--dark-5)") => {
    switch (feature) {
      case EProjectFeature.Datasource:
        return <DatabaseSvg width={16} height={16} color={color} />;
      case EProjectFeature.Workflow:
        return <AutomationSvg width={16} height={16} color={color} />;
      case EProjectFeature.EmailTemplate:
        return <MailOpenedSvg width={16} height={16} color={color} />;
      case EProjectFeature.PdfReport:
        return <FileTextSvg width={16} height={16} color={color} />;
      case EProjectFeature.ScheduledJobs:
        return <CalendarTimeSvg width={16} height={16} color={color} />;
      case EProjectFeature.ConfigurationManager:
        return <DeviceDesktopSvg width={16} height={16} color={color} />;
      case EProjectFeature.Archive:
        return <ArchiveSvg width={16} height={16} color={color} />;
      default:
        return <ProjectIcon width={16} height={16} color={color} />;
    }
  };

  // Human-readable label for each template feature, shown next to its icon in the
  // "Template Includes" hover popover. Mirrors the getFeatureIcon mapping.
  getFeatureLabel = (feature: EProjectFeature): string => {
    switch (feature) {
      case EProjectFeature.Datasource:
        return "Datasource";
      case EProjectFeature.Workflow:
        return "Workflow";
      case EProjectFeature.EmailTemplate:
        return "Email Template";
      case EProjectFeature.PdfReport:
        return "PDF Report";
      case EProjectFeature.ScheduledJobs:
        return "Scheduled Jobs";
      case EProjectFeature.ConfigurationManager:
        return "Configuration Manager";
      case EProjectFeature.Archive:
        return "Archive";
      default:
        return "";
    }
  };

  // Content of the "Template Includes" hover popover: the same feature icons shown on
  // the card badge, now each paired with its label. Driven by template.features (no
  // behaviour change — same data, richer presentation).
  renderTemplateIncludes = (features: EProjectFeature[]) => {
    return (
      <div className="kuika-project-list__template-includes">
        <KuikaSmallText className="kuika-project-list__template-includes__title">Template Includes</KuikaSmallText>
        <div className="kuika-project-list__template-includes__list">
          {features.map((feature: EProjectFeature) => (
            <div key={feature} className="kuika-project-list__template-includes__item">
              {this.getFeatureIcon(feature, "var(--dark-6)")}
              <KuikaSmallText>{this.getFeatureLabel(feature)}</KuikaSmallText>
            </div>
          ))}
        </div>
      </div>
    );
  };

  renderProjectActionBox = (title: string, image: string, owner: string, price?: string) => {
    return (
      <div
        className="kuika_project-list__action-box"
        onClick={() => {
          // const hasMobileAccess =
          //   KMainFunctions.isDomainHasAccess("Android") || KMainFunctions.isDomainHasAccess("IOS");
          // if (hasMobileAccess === false && type === EProjectType.Mobile) {
          //   this.props.showDomainAccessRequiredModal(["Android", "IOS"]);
          //   return;
          // }
          this.openProjectAddModal();
        }}
      >
        <div className="action-box__image-wrapper">
          <img src={image} alt="project" />
          <div className="action-box__btn-create">
            <KuikaButton>Create</KuikaButton>
          </div>
        </div>
        <div className="action-box__title-wrapper">
          <span className="action-box__title">{title}</span>
          <span className="action-box__owner">{owner}</span>
        </div>
        <div className="action-box__bottom">
          <div className="action-box__bottom__properties"></div>
          <span className="action-box__bottom__price">{price}</span>
        </div>
      </div>
    );
  };

  /**
   * One Templates tab's cards. A tab whose type matches nothing used to paint a blank grid, so it
   * says so instead — in the same page-level empty state the list itself shows when it has no apps.
   */
  renderTemplatePane = (templateType: string): React.ReactNode => {
    const isLoading: boolean = this.state.isTemplatesLoading || this.state.isDataLoading;
    // No blank-placeholder skip here: this endpoint returns only real templates. A blank can appear
    // only as stale home data mid-navigation, which the loading skeleton already masks. (Skipping
    // index 0 wrongly dropped a real template.)
    const templates: ITemplateProjectDto[] = this.state.templateProjects.filter(
      (p: ITemplateProjectDto): boolean =>
        // The pane's own key, not state: a leaving pane keeps drawing its tab.
        templateType === "all" ||
        (templateType === "web" && p.type === EProjectType.Desktop) ||
        (templateType === "mobile" && p.type === EProjectType.Mobile)
    );

    if (!isLoading && templates.length === 0) {
      return (
        <div className="kuika_project-list__empty-state">
          <KuikaEmptyState
            variant="page"
            svgName="search"
            customName="No templates in this category"
            description="Nothing here yet for this platform. Switch to All to see every template, or start from a Blank Project."
          />
        </div>
      );
    }

    return (
      <div
        style={{
          marginTop: "16px",
          display: "grid",
          gap: "24px",
          width: "100%",
          gridTemplateColumns: "repeat(auto-fill, 276px)"
        }}
      >
        {isLoading
          ? Array.from({ length: 6 }).map((_, index) => <div key={index}>{this.renderSkeletonCard()}</div>)
          : templates.map((template: ITemplateProjectDto) => this.renderCreateAppCard(template, true))}
      </div>
    );
  };

  renderSkeletonCard = () => {
    return (
      <div className="kuika-project-list__template-card kuika-project-list__template-card--skeleton" aria-hidden="true">
        <span className="kuika-project-list__template-card__skel-media" />
        <span className="kuika-project-list__template-card__skel-badge" />
        <div className="kuika-project-list__template-card__skel-overlay">
          <span className="kuika-project-list__template-card__skel-line kuika-project-list__template-card__skel-line--name" />
          <span className="kuika-project-list__template-card__skel-line kuika-project-list__template-card__skel-line--desc" />
        </div>
      </div>
    );
  };

  renderNewAppCard = (template: ITemplateProjectDto) => {
    const handleClick = (): void => {
      if (!template || template.projectId === Guid.EMPTY.toString()) {
        this.openProjectAddModal();
        return;
      }
      this.createTemplateProject(template.projectId);
    };
    return (
      <div
        data-testid="newappblankcard"
        data-onboarding-id="projects_new-app_blank-card"
        className="kuika-project-list__blank-card"
        onClick={handleClick}
      >
        <div className="kuika-project-list__blank-card__illustration">
          <div className="kuika-project-list__blank-card__illustration-box">
            <KuikaSvg
              name="dashed-square"
              width={40}
              height={40}
              color="var(--dark-5)"
              className="kuika-project-list__blank-card__illustration-frame"
            />
            <KuikaSvg name="plus" width={26} height={26} color="var(--dark-5)" />
          </div>
        </div>
        <div className="kuika-project-list__blank-card__text">
          <KuikaH5 className="kuika-project-list__blank-card__title">New App</KuikaH5>
          <div className="kuika-project-list__blank-card__desc">
            <KuikaText>Start from scratch.</KuikaText>
            <KuikaText>Create a new project from scratch</KuikaText>
          </div>
        </div>
        <div className="kuika-project-list__blank-card__badge">
          <KuikaSvg name="square-plus" width={12} height={12} color="var(--dark-6)" />
          <span className="kuika-project-list__blank-card__badge-text">Blank</span>
        </div>
      </div>
    );
  };

  renderCreateAppCard = (template: ITemplateProjectDto, isTemplate = false) => {
    const handleClick = (): void => {
      if (template.projectId === Guid.EMPTY.toString()) {
        this.openProjectAddModal();
        return;
      }
      this.createTemplateProject(template.projectId);
    };
    // Only features the UI can actually render (known icon + label). Seed data may carry values
    // outside EProjectFeature (e.g. dev fixtures) — skip them so the popover never shows a
    // fallback icon with an empty label, and keep the badge count in sync with what's listed.
    const knownFeatures: EProjectFeature[] = (template.features ?? []).filter(
      (feature: EProjectFeature) => this.getFeatureLabel(feature) !== ""
    );
    return (
      <div
        data-testid={`${template.name.toLowerCase().replace(/\s/g, "")}templatecard`}
        data-onboarding-id={`projects_templates_${template.name.toLowerCase().replace(/\s/g, "")}-card`}
        className={`kuika-project-list__template-card ${
          isTemplate ? "kuika-project-list__template-card--template" : ""
        }`}
        onClick={handleClick}
      >
        <img alt={template.name} src={template.image} className="kuika-project-list__template-card__image" />
        <div className="kuika-project-list__template-card__overlay">
          {template.category && (
            <KuikaSmallMediumText className="kuika-project-list__template-card__category">
              {template.category}
            </KuikaSmallMediumText>
          )}
          <KuikaTitle className="kuika-project-list__template-card__name">{template.name}</KuikaTitle>
          {template.description && (
            <KuikaSmallText className="kuika-project-list__template-card__desc">{template.description}</KuikaSmallText>
          )}
          {knownFeatures.length > 0 && (
            <TemplateIncludesBadge count={knownFeatures.length} content={this.renderTemplateIncludes(knownFeatures)} />
          )}
        </div>
        <div className="kuika-project-list__template-card__device-badge">
          <KuikaSvg
            name={template.type === EProjectType.Desktop ? "device-imac" : "device-mobile"}
            width={18}
            height={18}
            color="var(--white)"
          />
        </div>
        {template.isBeta && (
          <KuikaBadge content="BETA" color="purple" className="kuika-project-list__template-card__beta-badge" />
        )}
      </div>
    );
  };

  render() {
    const searchFilteredData: IProjectsResponseDto[] =
      KAgentHelper.filterArray(this.state.projectList, this.state.searchKeyWords) ?? [];
    const filteredData: IProjectsResponseDto[] =
      this.state.myAppsType === "all"
        ? searchFilteredData
        : searchFilteredData.filter((project: IProjectsResponseDto): boolean => {
            const projectType: EProjectType = this.getProjectType(project);
            if (this.state.myAppsType === "web") {
              return projectType === EProjectType.Desktop;
            }
            return projectType === EProjectType.Mobile;
          });

    const isList: boolean = this.props.projectListType !== EProjectListType.Templates;
    // The table draws its own skeleton rows while the first page is in flight, so it has to be
    // mounted before the rows arrive — gating it on `length > 0` left the whole list area blank
    // until the response landed. The empty state is what "no rows, and none coming" looks like.
    const showTable: boolean = isList && (filteredData.length > 0 || this.state.isDataLoading);
    const showEmptyState: boolean = isList && filteredData.length === 0 && !this.state.isDataLoading;

    return (
      <>
        {this.state.projectModalVisibility && (
          <ProjectDefinitionAddWithProjectType
            showModal={this.state.projectModalVisibility}
            closeModal={this.closeProjectAddModal}
            projectToEdit={this.state.projectToEdit}
            updateUserInfo={this.updateUserInfo}
            projectType={
              this.state.projectToEdit.templateProjectId
                ? this.state.projectToEdit.projectType === "web"
                  ? EProjectType.Desktop
                  : EProjectType.Mobile
                : undefined
            }
            templateProjectId={this.state.projectToEdit.templateProjectId}
          />
        )}
        {this.state.isDuplicateProjectModalActive && (
          <ProjectDuplicateModal
            projectId={this.state.projectIdToDuplicate}
            projectToDuplicate={this.state.projectToDuplicate}
            closeModal={this.closeDuplicateProjectModal}
            isRestoreProject={this.props.projectListType === EProjectListType.Archive}
          />
        )}
        <DeleteModal
          itemName={this.state.projectToDelete.name}
          triggerModal={this.state.triggerProjectDeleteModal}
          deleteItem={() => this.deleteProject()}
          deleteType={"App"}
          closeModal={() => this.setState({ triggerProjectDeleteModal: false })}
        />
        <TransferProjectOwnershipModal
          isModalOpen={this.state.isTransferOwnershipModalActive}
          closeModal={this.closeTransferOwnershipModal}
          projectId={this.state.projectIdToTransfer}
          projectName={this.state.projectNameToTransfer}
          sharedUsersForTransfer={this.state.sharedUsersForTransfer}
          loadProjectList={this.loadProjectList}
        />
        <div className="kuika_management__content-wrapper">
          {(this.state.templateProjects?.length > 0 || this.state.isTemplatesLoading) &&
            this.props.projectListType !== EProjectListType.Templates && (
              <div className="kuika-project-list__template-lists">
                <div className="kuika-project-list__template-list kuika-project-list__template-list--fixed">
                  <div className="kuika-project-list__template-list__header">
                    <KuikaSvg width={24} height={24} type="sidebar-icons" name="app" />
                    <KuikaH4 className="kuika-project-list__template-list__title">New App</KuikaH4>
                  </div>
                  <div className="kuika-project-list__template-list__items" style={{ gridTemplateColumns: "1fr" }}>
                    {this.state.isTemplatesLoading || this.state.isDataLoading
                      ? this.renderSkeletonCard()
                      : this.renderNewAppCard(this.state.templateProjects[0])}
                  </div>
                </div>
                <div className="kuika-project-list__template-list" style={{ flex: 1 }}>
                  <div
                    className="kuika-project-list__template-list__header"
                    style={{ justifyContent: "space-between" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <KuikaSvg width={24} height={24} name="template" />
                      <KuikaH4 className="kuika-project-list__template-list__title">Start with Templates</KuikaH4>
                    </div>
                    <KuikaButton
                      data-testid="seeAllButton"
                      data-onboarding-id="projects_templates_see-all"
                      type="secondary"
                      size="sm"
                      onClick={() => this.props.navigator.gotoTemplates()}
                    >
                      SEE ALL
                    </KuikaButton>
                  </div>
                  <div className="kuika-project-list__template-list__items kuika-project-list__template-list__items--auto-fill">
                    {this.state.isTemplatesLoading || this.state.isDataLoading ? (
                      <>
                        {this.renderSkeletonCard()}
                        {this.renderSkeletonCard()}
                      </>
                    ) : (
                      <>
                        {this.state.templateProjects
                          ?.slice(1)
                          .map((template, index) => this.renderCreateAppCard(template))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

          {this.props.projectListType === EProjectListType.Templates &&
            (this.state.templateProjects?.length > 0 || this.state.isTemplatesLoading) && (
              <div className="kuika_table_wrapper kuika_users_table_wrapper" style={{ padding: "24px 24px 24px 24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: "20px", fontWeight: "600", color: "var(--dark)" }}>
                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                      <KuikaSvg width={24} height={24} name="template" />

                      <KuikaH4 className="kuika-project-list__template-list__title">Templates</KuikaH4>
                      <div className="kuika-project-list__template-list__tabs">
                        <KuikaTabs
                          value={this.state.templateProjectType}
                          options={[
                            { value: "all", label: "All" },
                            { value: "web", label: "Web" },
                            { value: "mobile", label: "Mobile" }
                          ]}
                          onChange={(value: string) =>
                            this.setState({ templateProjectType: value as "all" | "web" | "mobile" })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* The grid is the tabs' pane, so changing tab slides the cards the way the
                    selection moved instead of cutting to a different set. */}
                <KuikaTabContent
                  activeKey={this.state.templateProjectType}
                  order={TEMPLATE_TYPE_ORDER}
                  render={(templateType: string): React.ReactNode => this.renderTemplatePane(templateType)}
                />
              </div>
            )}

          {this.props.projectListType !== EProjectListType.Templates && (
            <div className="kuika_project-list__header">
              <div className="kuika_project-list__header-left">
                <KuikaTextMedium className="kuika_project-list__title">{`${
                  this.props.projectListType === EProjectListType.Favorites ? "Favorite" : this.props.projectListType
                } Apps`}</KuikaTextMedium>
              </div>
              <div className="kuika_project-list__header-actions">
                <KuikaInput
                  size="md"
                  data-testid="searchAppInput"
                  data-onboarding-id="projects_list_search-input"
                  value={this.state.searchText}
                  placeholder="Search..."
                  className="kuika_project-list__search-input"
                  onChange={this.handleSearchInputChange}
                  leftIcon={<KuikaSvg name="search" type="common" width={12} height={12} color="var(--dark-7)" />}
                  rightIcon={
                    this.state.searchText ? (
                      <KuikaSvg
                        name="x"
                        type="common"
                        width={12}
                        height={12}
                        color="var(--dark-7)"
                        pointer
                        onClick={() => this.handleSearchInputChange({ target: { value: "" } })}
                      />
                    ) : undefined
                  }
                />
                <div className="kuika_project-list__type-filter">
                  <KuikaSelectbox
                    size="md"
                    placeholder="Type"
                    data-testid="projectTypeFilter"
                    // Scope the portalled option list so automation/tests can target
                    // these options without colliding with the template-type select.
                    overlayClassName="kuika_project-list__type-filter-overlay"
                    // "all" maps back to the resting "Type" placeholder so the filter reads as "no type filter"
                    value={this.state.myAppsType === "all" ? undefined : this.state.myAppsType}
                    onChange={(value: string | number) =>
                      this.setState({ myAppsType: (value as "all" | "web" | "mobile") ?? "all" })
                    }
                    prefix={<KuikaSvg name="layout-rows" type="common" width={12} height={12} color="var(--dark-7)" />}
                    options={[
                      { value: "all", label: <span data-testid="projectTypeOption-all">All</span> },
                      { value: "web", label: <span data-testid="projectTypeOption-web">Web</span> },
                      { value: "mobile", label: <span data-testid="projectTypeOption-mobile">Mobile</span> }
                    ]}
                  />
                </div>
              </div>
            </div>
          )}

          {showEmptyState && (
            <div className="kuika_project-list__empty-state">
              <KuikaEmptyState
                variant="page"
                svgName="search"
                customName={
                  this.props.projectListType === EProjectListType.Favorites
                    ? "No apps added to favorites"
                    : "No recent Apps found, start creating!"
                }
                description={
                  this.props.projectListType === EProjectListType.Favorites
                    ? "Once you favorite your apps, they will appear here for easy access."
                    : 'Start with "Blank Project" or choose one of templates to create a new app. Our platform provides you with the tools to develop applications quickly and easily. Everything you need to get started is right here!'
                }
              />
            </div>
          )}

          {showTable && (
            <div className="kuika_table_wrapper kuika_users_table_wrapper kuika-app-table-wrapper">
              <KuikaTable<IProjectsResponseDto>
                data={filteredData}
                columns={this.getTableColumns()}
                rowKey={(record) => record.id}
                pageSize={10}
                enableSorting
                enableColumnFilters
                enablePagination={filteredData.length > 10}
                // Keep the current page across reloads/sort, like the old antd Table.
                autoResetPageIndex={false}
                showHeader
                // Figma kolonları sabit-px + nowrap. Dar ekranda default "fixed" layout
                // overflow:hidden ile kolonları kesiyordu; "auto" ile kesme yerine
                // yatay scroll çıkar (overflow-x:auto), nowrap korunur.
                layoutMode="auto"
                loading={this.state.isDataLoading}
                loadingVariant="skeleton"
                data-testid="projectListTable"
                // A project row is addressed BY ENTITY: the name cell is middle-truncated into
                // head/tail spans, so row text is not an identity. The row's own id is.
                rowTestId={(row: IProjectsResponseDto): string => `projectRow-${row.name}`}
              />
            </div>
          )}
        </div>
      </>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName)),
    setProjectCounts: (params: ISetProjectCountsParams) => dispatch(setProjectCounts(params))
  };
};

const projectListBase = connect(
  null,
  mapDispatchToProps
)(
  withContext<IProjectListBaseProps>(
    withHistory<IProjectListBaseProps>(withManagement<IProjectListBaseProps>(ProjectListBase))
  )
);
export { projectListBase as ProjectListBase };
